import { call, put, takeLatest, all } from 'redux-saga/effects';
import {
    IGetRouteAliasPayload,
    types,
    IGetAllRouteAliasesPayload,
    ICreateRouteAliasPayload,
    IUpdateRouteAliasPayload,
    IDeleteRouteAliasPayload,
} from './types';
import {
    getRouteAliasFail,
    getRouteAliasSuccess,
    getAllRouteAliasesSuccess,
    getAllRouteAliasesFail,
    createRouteAliasSuccess,
    createRouteAliasFail,
    updateRouteAliasSuccess,
    updateRouteAliasFail,
    deleteRouteAliasSuccess,
    deleteRouteAliasFail,
} from './action';
import { api } from '../../configurations/api';
import { routeAliasUrl } from '../../configurations/api/url';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { Action } from 'redux';
import { navigate } from '../navigator/action';
import { globalErrorHandler } from '../error/saga';
import { showToastMessage } from '../../utils/AppUtils';

const cleanPath = (path: string) => path.replace(/^\/+/, '');

const hasMultipleSlashes = (path: string) => {
    return (path.match(/\//g) || []).length > 1;
};

function removeTrailingSlash(url: string): string {
    if (url.endsWith('/')) {
        return url.slice(0, -1);
    }
    return url;
}

function* getRouteAliasSaga({ payload }: { payload: IGetRouteAliasPayload }): any {
    try {
        const removedTrailingSlashes = removeTrailingSlash(payload.routePath);
        const hasMultiple = hasMultipleSlashes(removedTrailingSlashes);
        if (hasMultiple) {
            return;
        }
        const url = `${routeAliasUrl}/${cleanPath(removedTrailingSlashes)}`;
        const response = yield call(api, url, httpRequest.GET, null, 2, 1000);
        const data = response?.data?.data;
        const routeAlias = data;

        if (routeAlias && routeAlias.alternatePath && routeAlias.isActive && !routeAlias.deleted) {
            yield put(getRouteAliasSuccess(routeAlias));
            // Redirect to the alternate path
            yield put(
                navigate({
                    routes: routeAlias.alternatePath,
                    skipAuthentication: true,
                }),
            );
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getRouteAliasFail(error));
        // Don't show error to user, just let normal routing handle it
    }
}

function* getAllRouteAliasesSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IGetAllRouteAliasesPayload;
    resolve?: (data: any) => void;
    reject?: (error: any) => void;
}): any {
    try {
        const queryParams = new URLSearchParams();
        if (payload.page) queryParams.append('page', String(payload.page));
        if (payload.limit) queryParams.append('limit', String(payload.limit));
        if (payload.searchTerm) queryParams.append('searchTerm', payload.searchTerm);

        const url = `${routeAliasUrl}?${queryParams.toString()}`;
        const response = yield call(api, url, httpRequest.GET, null, 0, 0);
        yield put(getAllRouteAliasesSuccess(response));
        if (resolve) {
            resolve(response);
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getAllRouteAliasesFail(error));
        yield call(globalErrorHandler, error);
        if (reject) {
            reject(error);
        }
    }
}

function* createRouteAliasSaga({ payload }: { payload: ICreateRouteAliasPayload }): any {
    try {
        const response = yield call(api, routeAliasUrl, httpRequest.POST, payload, 0, 0);
        const { data } = response.data;
        yield put(createRouteAliasSuccess(data));
        yield call(showToastMessage, 'Route alias created successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createRouteAliasFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateRouteAliasSaga({ payload }: { payload: IUpdateRouteAliasPayload }): any {
    try {
        const { id, ...updateBody } = payload;
        const response = yield call(api, `${routeAliasUrl}/${id}`, httpRequest.PATCH, updateBody, 0, 0);
        const { data } = response.data;
        yield put(updateRouteAliasSuccess(data));
        yield call(showToastMessage, 'Route alias updated successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateRouteAliasFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteRouteAliasSaga({ payload }: { payload: IDeleteRouteAliasPayload }): any {
    try {
        yield call(api, `${routeAliasUrl}/${payload.id}`, httpRequest.DELETE, {}, 0, 0);
        yield put(deleteRouteAliasSuccess(payload.id));
        yield call(showToastMessage, 'Route alias deleted successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteRouteAliasFail(error));
        yield call(globalErrorHandler, error);
    }
}

interface GetRouteAliasAction extends Action {
    payload: IGetRouteAliasPayload;
}

interface GetAllRouteAliasesAction extends Action {
    payload: IGetAllRouteAliasesPayload;
    resolve?: (data: any) => void;
    reject?: (error: any) => void;
}

interface CreateRouteAliasAction extends Action {
    payload: ICreateRouteAliasPayload;
}

interface UpdateRouteAliasAction extends Action {
    payload: IUpdateRouteAliasPayload;
}

interface DeleteRouteAliasAction extends Action {
    payload: IDeleteRouteAliasPayload;
}

export default function* routeAliasSaga() {
    yield all([
        takeLatest<GetRouteAliasAction>(types.GET_ROUTE_ALIAS, getRouteAliasSaga),
        takeLatest<GetAllRouteAliasesAction>(types.GET_ALL_ROUTE_ALIASES, getAllRouteAliasesSaga),
        takeLatest<CreateRouteAliasAction>(types.CREATE_ROUTE_ALIAS, createRouteAliasSaga),
        takeLatest<UpdateRouteAliasAction>(types.UPDATE_ROUTE_ALIAS, updateRouteAliasSaga),
        takeLatest<DeleteRouteAliasAction>(types.DELETE_ROUTE_ALIAS, deleteRouteAliasSaga),
    ]);
}
